import { useEffect, useState, type FormEvent } from 'react';
import { supabase } from '@/lib/supabase';
import Modal from '@/components/base/Modal';
import { useAuth, type Profile } from '@/context/AuthContext';

interface AdminFormState {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  active: boolean;
}

const emptyAdmin: AdminFormState = {
  first_name: '',
  last_name: '',
  email: '',
  password: '',
  active: true,
};

async function invokeAdmin(body: Record<string, unknown>): Promise<Record<string, unknown>> {
  const { data, error } = await supabase.functions.invoke('manage_admin', { body });
  if (error) {
    let msg = error.message;
    const ctx = (error as { context?: { json?: () => Promise<{ error?: string }> } }).context;
    if (ctx?.json) {
      try {
        const j = await ctx.json();
        if (j?.error) msg = j.error;
      } catch {
        // ignore
      }
    }
    throw new Error(msg);
  }
  if (data?.error) throw new Error(data.error as string);
  return data as Record<string, unknown>;
}

function roleLabel(role: string): string {
  return role === 'super_admin' ? 'Súper Administrador' : 'Administrador';
}

export default function AdminSection() {
  const { user } = useAuth();
  const [admins, setAdmins] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Profile | null>(null);
  const [form, setForm] = useState<AdminFormState>(emptyAdmin);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [pwTarget, setPwTarget] = useState<Profile | null>(null);
  const [pwForm, setPwForm] = useState({ password: '', confirm: '' });
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwSaved, setPwSaved] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: e } = await supabase
        .from('profiles')
        .select('*')
        .in('role', ['super_admin', 'administrador'])
        .order('first_name');
      if (e) throw e;
      setAdmins((data as Profile[]) ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudieron cargar los administradores');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyAdmin });
    setFormError(null);
    setModalOpen(true);
  };

  const openEdit = (a: Profile) => {
    setEditing(a);
    setForm({
      first_name: a.first_name ?? '',
      last_name: a.last_name ?? '',
      email: a.email ?? '',
      password: '',
      active: !a.blocked,
    });
    setFormError(null);
    setModalOpen(true);
  };

  const handleSave = async (e?: FormEvent) => {
    if (e) e.preventDefault();
    setFormError(null);
    if (!form.first_name.trim() || !form.last_name.trim()) {
      setFormError('Nombre y apellido son obligatorios.');
      return;
    }
    if (!editing) {
      if (!form.email.trim()) {
        setFormError('El correo electrónico es obligatorio.');
        return;
      }
      if (form.password.length < 8) {
        setFormError('La contraseña debe tener al menos 8 caracteres.');
        return;
      }
    }
    setSaving(true);
    try {
      if (editing) {
        await invokeAdmin({
          action: 'update',
          user_id: editing.id,
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          active: form.active,
        });
      } else {
        await invokeAdmin({
          action: 'create',
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          email: form.email.trim(),
          password: form.password,
        });
      }
      setModalOpen(false);
      load();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'No se pudo guardar el administrador');
    } finally {
      setSaving(false);
    }
  };

  const openPassword = (a: Profile) => {
    setPwTarget(a);
    setPwForm({ password: '', confirm: '' });
    setPwError(null);
    setPwSaved(false);
  };

  const handlePassword = async () => {
    if (!pwTarget) return;
    setPwError(null);
    setPwSaved(false);
    if (pwForm.password.length < 8) {
      setPwError('La contraseña debe tener al menos 8 caracteres.');
      return;
    }
    if (pwForm.password !== pwForm.confirm) {
      setPwError('Las contraseñas no coinciden.');
      return;
    }
    setSaving(true);
    try {
      await invokeAdmin({ action: 'reset_password', user_id: pwTarget.id, password: pwForm.password });
      setPwSaved(true);
      setPwTarget(null);
    } catch (err) {
      setPwError(err instanceof Error ? err.message : 'No se pudo cambiar la contraseña');
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    'w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400';
  const labelClass = 'block text-sm font-medium text-foreground-800 mb-1.5';

  return (
    <div>
      <div className="flex justify-end">
        <button
          onClick={openCreate}
          className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 px-4 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition cursor-pointer"
        >
          <i className="ri-user-add-line" /> Crear administrador
        </button>
      </div>

      {error && (
        <div className="mt-4 rounded-lg border border-accent-200 bg-accent-50 px-4 py-3 text-sm text-foreground-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="mt-10 flex items-center justify-center py-16">
          <div className="w-10 h-10 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
        </div>
      ) : admins.length === 0 ? (
        <div className="mt-10 rounded-lg border border-dashed border-background-300 p-12 text-center">
          <i className="ri-shield-user-line text-4xl text-foreground-400" />
          <p className="mt-3 text-foreground-600">Aún no hay administradores registrados.</p>
        </div>
      ) : (
        <div className="mt-6 rounded-lg border border-background-200 overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead className="bg-background-100 text-foreground-600 text-left">
              <tr>
                <th className="px-4 py-3 font-medium">Administrador</th>
                <th className="px-4 py-3 font-medium">Correo</th>
                <th className="px-4 py-3 font-medium">Rol</th>
                <th className="px-4 py-3 font-medium">Estado</th>
                <th className="px-4 py-3 font-medium text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-200">
              {admins.map((a) => (
                <tr key={a.id} className="hover:bg-background-100 transition">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center font-semibold shrink-0">
                        {((a.first_name || '?')[0] || '?').toUpperCase()}
                      </div>
                      <span className="font-medium text-foreground-950">
                        {[a.first_name, a.last_name].filter(Boolean).join(' ')}
                        {a.id === user?.id && (
                          <span className="ml-2 text-xs font-normal text-foreground-500">(tú)</span>
                        )}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-foreground-700">
                    <span className="text-xs">{a.email || '—'}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                        a.role === 'super_admin'
                          ? 'bg-primary-100 text-primary-700'
                          : 'bg-secondary-100 text-secondary-900'
                      }`}
                    >
                      {roleLabel(a.role)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                        a.blocked ? 'bg-secondary-100 text-secondary-900' : 'bg-accent-100 text-accent-900'
                      }`}
                    >
                      {a.blocked ? 'Inactivo' : 'Activo'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => openEdit(a)}
                        className="w-8 h-8 flex items-center justify-center rounded-md text-primary-700 hover:bg-primary-50 cursor-pointer"
                        title="Editar"
                      >
                        <i className="ri-edit-line" />
                      </button>
                      <button
                        onClick={() => openPassword(a)}
                        className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-600 hover:bg-background-100 cursor-pointer"
                        title="Cambiar contraseña"
                      >
                        <i className="ri-key-2-line" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Crear / Editar administrador */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Editar administrador' : 'Crear administrador'}
        footer={
          <>
            <button
              onClick={() => setModalOpen(false)}
              disabled={saving}
              className="rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
            >
              Cancelar
            </button>
            <button
              onClick={() => handleSave()}
              disabled={saving}
              className="rounded-md bg-primary-500 hover:bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition disabled:opacity-60 cursor-pointer"
            >
              {saving ? 'Guardando...' : editing ? 'Guardar' : 'Crear administrador'}
            </button>
          </>
        }
      >
        <form onSubmit={handleSave} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Nombre *</label>
              <input
                value={form.first_name}
                onChange={(e) => setForm((p) => ({ ...p, first_name: e.target.value }))}
                placeholder="María"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Apellido *</label>
              <input
                value={form.last_name}
                onChange={(e) => setForm((p) => ({ ...p, last_name: e.target.value }))}
                placeholder="García"
                className={inputClass}
              />
            </div>
          </div>
          {!editing ? (
            <>
              <div>
                <label className={labelClass}>Correo electrónico *</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                  placeholder="maria@negocio.com"
                  className={inputClass}
                />
                <p className="mt-1 text-xs text-foreground-500">
                  Con este correo y la contraseña el administrador ingresará al sistema.
                </p>
              </div>
              <div>
                <label className={labelClass}>Contraseña *</label>
                <input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                  placeholder="Mínimo 8 caracteres"
                  className={inputClass}
                />
                <p className="mt-1 text-xs text-foreground-500">
                  Puedes cambiarla más adelante desde el botón de contraseña.
                </p>
              </div>
            </>
          ) : (
            <div className="rounded-md border border-background-200 p-3">
              <p className="text-xs font-medium text-foreground-500 uppercase tracking-wide">Correo</p>
              <p className="mt-1 text-sm text-foreground-900 break-all">{form.email || '—'}</p>
            </div>
          )}
          {editing && (
            <label className="flex items-center justify-between gap-3 rounded-md border border-background-200 p-3 cursor-pointer">
              <span className="text-sm font-medium text-foreground-900">Cuenta activa</span>
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm((p) => ({ ...p, active: e.target.checked }))}
                className="w-5 h-5 accent-primary-500 cursor-pointer"
              />
            </label>
          )}
          {formError && (
            <div className="rounded-md border border-accent-200 bg-accent-50 px-3 py-2.5 text-sm text-foreground-700">
              {formError}
            </div>
          )}
        </form>
      </Modal>

      {/* Cambiar contraseña */}
      <Modal
        open={!!pwTarget}
        onClose={() => setPwTarget(null)}
        title="Cambiar contraseña"
        footer={
          <>
            <button
              onClick={() => setPwTarget(null)}
              disabled={saving}
              className="rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
            >
              Cancelar
            </button>
            <button
              onClick={handlePassword}
              disabled={saving}
              className="rounded-md bg-primary-500 hover:bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition disabled:opacity-60 cursor-pointer"
            >
              {saving ? 'Guardando...' : 'Cambiar contraseña'}
            </button>
          </>
        }
      >
        {pwTarget && (
          <div className="space-y-4">
            <p className="text-sm text-foreground-700">
              Estás cambiando la contraseña de{' '}
              <span className="font-medium text-foreground-950">
                {[pwTarget.first_name, pwTarget.last_name].filter(Boolean).join(' ')}
              </span>
              .
            </p>
            <div>
              <label className={labelClass}>Nueva contraseña *</label>
              <input
                type="password"
                value={pwForm.password}
                onChange={(e) => setPwForm((p) => ({ ...p, password: e.target.value }))}
                placeholder="Mínimo 8 caracteres"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Confirmar contraseña *</label>
              <input
                type="password"
                value={pwForm.confirm}
                onChange={(e) => setPwForm((p) => ({ ...p, confirm: e.target.value }))}
                placeholder="Repite la contraseña"
                className={inputClass}
              />
            </div>
            {pwTarget.id === user?.id && (
              <div className="flex items-start gap-2 rounded-md bg-secondary-100 px-3 py-2.5 text-sm text-secondary-900">
                <i className="ri-information-line mt-0.5" />
                <span>Al cambiar tu propia contraseña se cerrará tu sesión actual.</span>
              </div>
            )}
            {pwError && (
              <div className="rounded-md border border-accent-200 bg-accent-50 px-3 py-2.5 text-sm text-foreground-700">
                {pwError}
              </div>
            )}
            {pwSaved && (
              <div className="rounded-md border border-accent-200 bg-accent-50 px-3 py-2.5 text-sm text-accent-900">
                Contraseña actualizada correctamente.
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}